import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../shadcn/components/ui/Button";
import { Input } from "../shadcn/components/ui/Input";
import logo from "../assets/logo.png";

import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../shadcn/components/ui/Sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn/components/ui/Dropdown-menu";
import {
  Facebook,
  Instagram,
  Menu,
  PhoneIcon as WhatsApp,
  XIcon as TwitterX,
  ChevronDown,
  Search,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../shadcn/components/ui/Avatar";
import { useState, useEffect } from "react";

// Function to get dynamic dashboard path
const getDashboardPath = (userRole) => {
  switch (userRole) {
    case "Individual":
      return "/dashboard/My profile";
    case "Property Owner":
      return "/propertyownerdashboard";
    case "Hospital System/Managed Care Organizations":
      return "/HospitalManagement";
    case "Real Estate Professionals":
      return "/realestatedashboard";
    case "Service Provider":
      return "/servicedashboard";
    case "Admin":
      return "/superadmin";
    case "Non Profits":
      return "/nonprofit";
    case "Super Admin":
      return "/superadmin";
    default:
      return "/";
  }
};

export function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [username, setUsername] = useState("");
  const navigate = useNavigate();

  const [authToken, setAuthToken] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Search states
  const [propertySearch, setPropertySearch] = useState({
    city: "",
    state: "",
    zip: "",
  });

  const [serviceSearch, setServiceSearch] = useState({
    transport: "",
    general: "",
  });

  // Simulated function to fetch user data from local storage or API
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const role = localStorage.getItem("userRole");
    setAuthToken(token);
    setUserRole(role);
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("name");
    localStorage.removeItem("userRole");
    localStorage.removeItem("countryOfBirth");
    localStorage.removeItem("birthDate");
    localStorage.removeItem("education");
    localStorage.removeItem("email");
    localStorage.removeItem("formid");
    localStorage.removeItem("phonenumber");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("propertyData");
    setAuthToken(null);
    setUserRole(null);
    navigate("/login");
  };

  useEffect(() => {
    const storedName = localStorage.getItem("name");
    if (storedName) {
      setUsername(storedName);
    }
  }, []);

  // Handle dashboard redirection
  const handleDashboardNavigation = () => {
    if (userRole) {
      switch (userRole) {
        case "Individual":
          navigate("/dashboard/My profile");
          break;
        case "Property Owner":
          navigate("/propertyownerdashboard");
          break;
        case "Hospital System/Managed Care Organizations":
          navigate("/HospitalManagement");
          break;
        case "Real Estate Professionals":
          navigate("/realestatedashboard");
          break;
        case "Service Provider":
          navigate("/servicedashboard");
          break;
        case "Non Profits":
          navigate("/nonprofit");
          break;
        case "Super Admin":
          navigate("/superadmin");
          break;
        default:
          navigate("/");
      }
    }
  };

  // Property search handler
  const handlePropertySearch = () => {
    const queryParams = new URLSearchParams();
    if (propertySearch.city.trim())
      queryParams.set("city", propertySearch.city.trim());
    if (propertySearch.state.trim())
      queryParams.set("state", propertySearch.state.trim());
    if (propertySearch.zip.trim())
      queryParams.set("zip", propertySearch.zip.trim());

    navigate(`/property?${queryParams.toString()}`);
  };

  // Service search handler
  const handleServiceSearch = () => {
    const queryParams = new URLSearchParams();
    if (serviceSearch.transport.trim())
      queryParams.set("transport", serviceSearch.transport.trim());
    if (serviceSearch.general.trim())
      queryParams.set("general", serviceSearch.general.trim());

    navigate(`/services?${queryParams.toString()}`);
  };

  // Global search functionality
  const searchableItems = [
    { label: "Profile", path: getDashboardPath(userRole) },
    { label: "Properties", path: "/property" },
    { label: "Services", path: "/services" },
    { label: "Community Resource", path: "/community" },
    { label: "About Us", path: "/about" },
    { label: "Login/Register", path: "/Login" },
  ];

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    const filteredSuggestions = searchableItems.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase())
    );
    setSuggestions(filteredSuggestions);
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setSuggestions(searchableItems);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsInputFocused(false);
      setSuggestions([]);
    }, 200);
  };

  const handleSuggestionClick = (path) => {
    navigate(path);
    setSearchQuery("");
    setSuggestions([]);
  };

  return (
    <div className="relative">
      {/* Main Navigation */}
      <nav className="relative z-20  bg-gray-900/95 text-white">
        <div className="container mx-auto lg:px-0 px-5">
          <div className="flex h-28 md:h-40 items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1 md:gap-2">
              <div className="h-20 w-20 mr-1 lg:h-36 lg:w-36 overflow-hidden rounded-full">
                <img
                  src={logo}
                  alt="Silver TLC"
                  className="h-full w-full object-cover"
                />
              </div>
              <span className="text-2xl lg:text-6xl  font-bold font">
                SilverTLC
              </span>
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 gap-4">
              {/* Property Search */}
              <div className="flex items-center gap-2 w-full">
                <Link to={"/login"}>
                  <Button className="bg-[#8024D1] hover:bg-[#9941e7]">
                    Property Search
                  </Button>
                </Link>
              </div>

              {/* Service Search */}
              <div className="flex items-center gap-2 w-full">
                <Link to={"/login"}>
                  <Button className="bg-[#8024D1] hover:bg-[#9941e7] w-full">
                    Service Search <span> </span>
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Side Content */}
            <div className="flex items-center md:gap-2">
              {/* Mobile Menu Trigger - Now on right side */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[300px] bg-gray-900 text-white"
                >
                  <div className="mt-6 space-y-4">
                    {/* Navigation Links */}
                    <Link to="/" className="block px-4 py-2 hover:bg-gray-800">
                      Home
                    </Link>
                    <Link
                      to="/property"
                      className="block px-4 py-2 hover:bg-gray-800"
                    >
                      Property
                    </Link>
                    <Link
                      to="/services"
                      className="block px-4 py-2 hover:bg-gray-800"
                    >
                      Services
                    </Link>

                    <Link
                      to="/about"
                      className="block px-4 py-2 hover:bg-gray-800"
                    >
                      About Us
                    </Link>

                    {/* Auth Buttons */}
                    <div className="pt-4 border-t border-gray-700">
                      {authToken ? (
                        <>
                          <button
                            onClick={handleDashboardNavigation}
                            className="w-full text-left px-4 py-2 hover:bg-gray-800"
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={handleLogout}
                            className="w-full text-left py-2 hover:bg-gray-800 text-red-400"
                          >
                            Logout
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            className="block py-2 hover:bg-gray-800"
                          >
                            Login
                          </Link>
                          <Link
                            to="/register"
                            className="block px-4 py-2 hover:bg-gray-800 hover:text-gray-200"
                          >
                            Register
                          </Link>
                        </>
                      )}
                    </div>

                    {/* Chat Button */}
                    <div className="pt-4 border-t border-gray-700">
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-800">
                        Chat
                      </button>
                    </div>

                    {/* Social Icons */}
                    <div className="pt-4 border-t border-gray-700">
                      <div className="flex justify-center gap-4 px-4 py-2">
                        <Instagram className="h-5 w-5 text-purple-400" />
                        <WhatsApp className="h-5 w-5 text-purple-400" />
                        <TwitterX className="h-5 w-5 text-purple-400" />
                        <Facebook className="h-5 w-5 text-purple-400" />
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Right Side Content (Desktop only) */}
              <div className="hidden md:flex flex-col items-end">
                <div className="flex items-center space-x-0">
                  {" "}
                  {/* Changed to space-x-0 */}
                  <Link to="/register">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-gray-800 hover:text-gray-200 px-1" // Reduced padding
                    >
                      <span className="text-sm hover:text-gray-200">
                        Register
                      </span>
                    </Button>
                  </Link>
                  <span className="text-white px-0">|</span>{" "}
                  {/* Removed padding */}
                  {authToken ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-gray-800 hover:text-gray-200 px-1" // Reduced padding
                      onClick={handleLogout}
                    >
                      <span className="text-sm">Logout</span>
                    </Button>
                  ) : (
                    <Link to="/login">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-gray-800 hover:text-gray-200 px-1" // Reduced padding
                      >
                        <span className="text-sm">Login</span>
                      </Button>
                    </Link>
                  )}
                </div>
                <div className="flex items-center flex-col gap-2">
                  <Link
                    to="/about"
                    className="text-white hover:text-gray-300 text-sm px-2 py-1 hover:bg-gray-800 rounded-md transition-colors"
                  >
                    About Us
                  </Link>
                </div>

                <div className="flex items-center gap-2">
                  {authToken && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full bg-transparent text-black hover:bg-gray-800 h-8 w-8"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src="" alt="User" />
                            <AvatarFallback>
                              {username
                                ? username.charAt(0).toUpperCase()
                                : "U"}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-gray-800 text-white border-gray-700"
                      >
                        <DropdownMenuItem
                          onClick={handleDashboardNavigation}
                          className="cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                        >
                          Dashboard
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
